import { z } from 'zod';
import { NIGERIAN_PHONE_REGEX, PASSWORD_MIN_LENGTH } from '../constants';
import { ServiceType, TurnaroundTime, ItemCategory, ItemCondition } from '../types/order';
import { PaymentMethod } from '../types/payment';

/** Phone number validation — Nigerian format +234XXXXXXXXXX */
export const phoneSchema = z
  .string()
  .regex(NIGERIAN_PHONE_REGEX, 'Phone must be in +234XXXXXXXXXX format');

/** Password — min 12 chars, 1 upper, 1 number, 1 special */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .regex(/[A-Z]/, 'Password must contain at least 1 uppercase letter')
  .regex(/[0-9]/, 'Password must contain at least 1 number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least 1 special character');

/** Customer signup — maps to PRD FR-1.1 */
export const signupSchema = z.object({
  phone: phoneSchema,
  password: passwordSchema,
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email().optional(),
});

/** Login */
export const loginSchema = z.object({
  identifier: z.string().min(1, 'Email or phone is required'),
  password: z.string().min(1),
});

/** OTP verification */
export const otpSchema = z.object({
  phone: phoneSchema,
  code: z.string().length(6).regex(/^[0-9]+$/, 'OTP must be 6 digits'),
});

/** Address creation — maps to PRD FR-1.3 */
export const addressSchema = z.object({
  label: z.string().min(1).max(50),
  addressLine1: z.string().min(1).max(255),
  addressLine2: z.string().max(255).optional(),
  city: z.string().min(1).max(100),
  state: z.string().min(1).max(100),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  specialInstructions: z.string().max(500).optional(),
  isDefault: z.boolean().default(false),
});

/** Order item entry — maps to PRD FR-2.2 */
export const orderItemSchema = z.object({
  category: z.nativeEnum(ItemCategory),
  catalogId: z.string().optional(),
  description: z.string().max(255).optional(),
  quantity: z.number().int().min(1).max(100),
  specialInstructions: z.string().max(500).optional(),
});

/** Order creation — maps to PRD FR-2.5 */
export const createOrderSchema = z.object({
  serviceType: z.nativeEnum(ServiceType),
  turnaroundTime: z.nativeEnum(TurnaroundTime),
  items: z.array(orderItemSchema).min(1),
  pickupAddressId: z.string().min(1),
  deliveryAddressId: z.string().min(1),
  pickupDate: z.string(),
  pickupTimeSlot: z.string(),
  deliveryDate: z.string().optional(), // auto-calculated
  deliveryTimeSlot: z.string().optional(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  promoCode: z.string().max(50).optional(),
  notes: z.string().max(1000).optional(),
});

/** Driver pickup item recording — maps to PRD FR-11.3 enhanced */
export const pickupItemRecordSchema = z.object({
  category: z.nativeEnum(ItemCategory),
  quantity: z.number().int().min(1).max(100),
  condition: z.nativeEnum(ItemCondition).optional(),
  conditionNotes: z.string().max(500).optional(),
});

/** Driver complete pickup — marks order as picked up */
export const completePickupSchema = z.object({
  routeStopId: z.string().min(1),
  orderId: z.string().min(1),
  items: z.array(pickupItemRecordSchema).min(1),
  signatureDataUrl: z.string().min(1),
  photoUrls: z.array(z.string()).optional(),
  notes: z.string().max(1000).optional(),
});

/** Driver fail pickup */
export const failPickupSchema = z.object({
  routeStopId: z.string().min(1),
  reason: z.enum([
    'customer_not_home',
    'wrong_address',
    'customer_cancelled',
    'customer_unavailable',
    'other',
  ]),
  notes: z.string().max(500).optional(),
});

/** POS walk-in order — FR-10.1 */
export const posOrderSchema = z.object({
  customerPhone: phoneSchema,
  customerFirstName: z.string().min(1).max(100),
  customerLastName: z.string().min(1).max(100),
  customerEmail: z.string().email().optional(),
  serviceType: z.nativeEnum(ServiceType),
  turnaroundTime: z.nativeEnum(TurnaroundTime),
  items: z.array(z.object({
    category: z.nativeEnum(ItemCategory),
    catalogId: z.string().optional(),
    quantity: z.number().int().min(1).max(100),
    specialInstructions: z.string().max(500).optional(),
    color: z.string().max(50).optional(),
    fabricDamageType: z.string().max(50).optional(),
    fabricDamageNotes: z.string().max(500).optional(),
    brandName: z.string().max(100).optional(),
  })).min(1),
  paymentMethod: z.nativeEnum(PaymentMethod),
  amountPaidKobo: z.number().int().min(0).optional(),
  expressSurchargeKobo: z.number().int().min(0).optional(),
  customDeliveryDate: z.string().optional(),
  readyTime: z.string().optional(), // pickup/delivery time of day, e.g. "17:00" (default 5pm)
  notes: z.string().max(1000).optional(),
  photoUrls: z.array(z.string()).max(5).optional(), // intake evidence photos captured at pickup
  source: z.enum(['pos', 'phone', 'walk_in']).default('pos'),
});

/** POS cash drawer open — FR-10.6 */
export const cashDrawerOpenSchema = z.object({
  openingFloatKobo: z.number().int().min(0),
});

/** POS cash drawer close — FR-10.6 */
export const cashDrawerCloseSchema = z.object({
  actualCashKobo: z.number().int().min(0),
  notes: z.string().max(500).optional(),
});

/** POS cash payout — FR-10.6 */
export const cashPayoutSchema = z.object({
  amountKobo: z.number().int().min(1),
  reason: z.string().min(1).max(255),
  approvedBy: z.string().optional(),
});

/** Driver clock in — FR-11.1 */
export const clockInSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  deviceId: z.string().min(1),
});

/** Driver clock out — FR-11.6 */
export const clockOutSchema = z.object({
  actualCashBalanceKobo: z.number().int().min(0),
  notes: z.string().max(500).optional(),
});

/** Driver complete delivery — FR-11.4 */
export const completeDeliverySchema = z.object({
  routeStopId: z.string().min(1),
  orderId: z.string().min(1),
  signatureDataUrl: z.string().min(1),
  photoUrls: z.array(z.string()).max(5).optional(),
  cashCollectedKobo: z.number().int().min(0).optional(), // COD
  notes: z.string().max(1000).optional(),
});

/** Driver fail delivery — FR-11.5 */
export const failDeliverySchema = z.object({
  routeStopId: z.string().min(1),
  reason: z.enum([
    'customer_not_home',
    'customer_refused',
    'wrong_address',
    'customer_unavailable',
    'other',
  ]),
  notes: z.string().max(500).optional(),
});

/** Driver GPS location update — FR-11.7 */
export const locationUpdateSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  accuracy: z.number().optional(),
  heading: z.number().optional(),
  speed: z.number().optional(),
});

/** Passkey registration — begin */
export const passkeyRegisterBeginSchema = z.object({
  friendlyName: z.string().max(100).optional(), // "iPhone Face ID"
});

/** Passkey registration — complete */
export const passkeyRegisterCompleteSchema = z.object({
  challengeId: z.string().min(1),
  credential: z.object({
    id: z.string().min(1), // base64url credential ID
    rawId: z.string().min(1), // base64url
    type: z.literal('public-key'),
    response: z.object({
      attestationObject: z.string().min(1), // base64url
      clientDataJSON: z.string().min(1), // base64url
    }),
    authenticatorAttachment: z.enum(['platform', 'cross-platform']).optional(),
  }),
  friendlyName: z.string().max(100).optional(),
});

/** Passkey authentication — begin */
export const passkeyAuthBeginSchema = z.object({
  phone: phoneSchema.optional(), // optional: allows discoverable credentials
});

/** Passkey authentication — complete */
export const passkeyAuthCompleteSchema = z.object({
  challengeId: z.string().min(1),
  credential: z.object({
    id: z.string().min(1),
    rawId: z.string().min(1),
    type: z.literal('public-key'),
    response: z.object({
      authenticatorData: z.string().min(1),
      clientDataJSON: z.string().min(1),
      signature: z.string().min(1),
      userHandle: z.string().optional(), // base64url user ID
    }),
  }),
});

/** Password reset request */
export const resetPasswordRequestSchema = z.object({
  phone: phoneSchema,
});

/** Password reset confirm */
export const resetPasswordConfirmSchema = z.object({
  phone: phoneSchema,
  code: z.string().length(6).regex(/^[0-9]+$/, 'OTP must be 6 digits'),
  newPassword: passwordSchema,
});

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type OtpInput = z.infer<typeof otpSchema>;
export type AddressInput = z.infer<typeof addressSchema>;
export type OrderItemInput = z.infer<typeof orderItemSchema>;
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type PickupItemRecordInput = z.infer<typeof pickupItemRecordSchema>;
export type CompletePickupInput = z.infer<typeof completePickupSchema>;
export type FailPickupInput = z.infer<typeof failPickupSchema>;
export type PasskeyRegisterBeginInput = z.infer<typeof passkeyRegisterBeginSchema>;
export type PasskeyRegisterCompleteInput = z.infer<typeof passkeyRegisterCompleteSchema>;
export type PasskeyAuthBeginInput = z.infer<typeof passkeyAuthBeginSchema>;
export type PasskeyAuthCompleteInput = z.infer<typeof passkeyAuthCompleteSchema>;
export type ResetPasswordRequestInput = z.infer<typeof resetPasswordRequestSchema>;
export type ResetPasswordConfirmInput = z.infer<typeof resetPasswordConfirmSchema>;
export type ClockInInput = z.infer<typeof clockInSchema>;
export type ClockOutInput = z.infer<typeof clockOutSchema>;
export type CompleteDeliveryInput = z.infer<typeof completeDeliverySchema>;
export type FailDeliveryInput = z.infer<typeof failDeliverySchema>;
export type LocationUpdateInput = z.infer<typeof locationUpdateSchema>;
export type PosOrderInput = z.infer<typeof posOrderSchema>;
export type CashDrawerOpenInput = z.infer<typeof cashDrawerOpenSchema>;
export type CashDrawerCloseInput = z.infer<typeof cashDrawerCloseSchema>;
export type CashPayoutInput = z.infer<typeof cashPayoutSchema>;
